import {
  combineLatest, concatMap, debounceTime, delay, distinctUntilChanged, exhaustMap,
  filter, from, map, merge, NEVER, of, pipe, ReplaySubject, startWith,
  switchMap, take, takeUntil, takeWhile, withLatestFrom
} from 'rxjs';
import { MONITORING_CONSENTED } from '../../core/monitoring/types';
import { Disposable } from '../../core';
import { hashMessage, injectNielsenScript } from '../../../externals/utils';
import {
  NIELSEN_DEBUG, NIELSEN_EVENT_END, NIELSEN_EVENT_LOADEDMETADATA,
  NIELSEN_EVENT_SET_PLAYHEAD_POSITION, NIELSEN_EVENT_STOP, NIELSEN_INSTANCE_NAME, NIELSEN_LIB_NAME
} from './const';
import { PLAYBACK_END, PLAYBACK_PAUSE, PLAYBACK_PLAY, PLAYBACK_PLAYING, PLAYBACK_STOPPED, PLAYBACK_WAITING, PLAYER_ERROR, RENDERER_READY, TIMEUPDATE } from '../../store/types';
import { FIRST_PLAY, PAUSE, SEEK } from '../../core/command/types';
import { EPG_CURRENT_CHANGED, EPG_CURRENT_START, TIMESHIFTING_MEDIAMETRIE_LIVE, TIMESHIFTING_MEDIAMETRIE_SHIFTING } from '../../core/timeshifting/types';
import { DISPOSED, NEW_CONTENT, NEXT, USER_SEEK_START, VIDEO_START } from '../../types';
import { ADBREAK_HOOK, INTERRUPTION_DID_START } from '../../core/interruptor/types';
import Media from '../../core/media/Media';
import { slidingBufferCount } from '../../utils/rx-utils';

const LIVE = 'LIVE';
const TIMESHIFTING = 'TIMESHIFTING';
const REPLAY = 'REPLAY';

const EVENT_MAPPING = {
  [LIVE]: {
    [EPG_CURRENT_START]: NIELSEN_EVENT_SET_PLAYHEAD_POSITION,
    [PLAYBACK_PAUSE]: NIELSEN_EVENT_STOP,
    [PLAYBACK_END]: NIELSEN_EVENT_END,
    [PLAYBACK_STOPPED]: NIELSEN_EVENT_END,
    [NEXT]: NIELSEN_EVENT_END,
    [PLAYER_ERROR]: NIELSEN_EVENT_END,
    [EPG_CURRENT_CHANGED]: NIELSEN_EVENT_END,
    [TIMESHIFTING_MEDIAMETRIE_LIVE]: NIELSEN_EVENT_END,
    [TIMESHIFTING_MEDIAMETRIE_SHIFTING]: NIELSEN_EVENT_END
  },
  [REPLAY]: {
    [PLAYBACK_PAUSE]: NIELSEN_EVENT_STOP,
    [PLAYBACK_WAITING]: NIELSEN_EVENT_STOP,
    [SEEK]: NIELSEN_EVENT_STOP,
    [PLAYBACK_END]: NIELSEN_EVENT_END,
    [PLAYBACK_STOPPED]: NIELSEN_EVENT_END,
    [NEXT]: NIELSEN_EVENT_END,
    [PLAYER_ERROR]: NIELSEN_EVENT_END
  },
  [TIMESHIFTING]: {
    [EPG_CURRENT_START]: NIELSEN_EVENT_SET_PLAYHEAD_POSITION,
    [PLAYBACK_PAUSE]: NIELSEN_EVENT_STOP,
    [PLAYBACK_WAITING]: NIELSEN_EVENT_STOP,
    [SEEK]: NIELSEN_EVENT_STOP,
    [PLAYBACK_END]: NIELSEN_EVENT_END,
    [PLAYBACK_STOPPED]: NIELSEN_EVENT_END,
    [NEXT]: NIELSEN_EVENT_END,
    [PLAYER_ERROR]: NIELSEN_EVENT_END,
    [TIMESHIFTING_MEDIAMETRIE_LIVE]: NIELSEN_EVENT_END,
    [TIMESHIFTING_MEDIAMETRIE_SHIFTING]: NIELSEN_EVENT_END,
    [EPG_CURRENT_CHANGED]: NIELSEN_EVENT_END
  }
};

const allowedEvents = Object.keys(EVENT_MAPPING).reduce(
  (acc, key) => [...acc, Object.keys(EVENT_MAPPING[key])]
    .filter((v, i, a) => a.indexOf(v) === i),
  []
).flat();

export default class Nielsen extends Disposable {
  constructor(player) {
    super(player);
    injectNielsenScript(NIELSEN_LIB_NAME);
    this.mediasUpdated$ = new ReplaySubject(1);
    this.isShifting$ = new ReplaySubject(1);
    this.nSdkInstance = null;
    this.events$ = new ReplaySubject(1);

    this.instanceName = `${NIELSEN_INSTANCE_NAME}_${Math.floor(Math.random() * 10000000000)}`;

    Nielsen.isShiftingStream(player.events$, player.medias$).subscribe(this.isShifting$);
    Nielsen.haltForMidroll(player).subscribe(this.events$);
    Nielsen.disposedEventToNielsenEventEnd(player.events$).subscribe(this.events$);

    Nielsen.createEventNotificationStream({
      player,
      mediasUpdated$: this.mediasUpdated$,
      isShifting$: this.isShifting$
    }).subscribe(this.events$);

    this.nlsQ = window[NIELSEN_LIB_NAME].nlsQ;

    Nielsen.createInitStream(player).subscribe(
      this.createSdkInstance.bind(this)
    );

    Nielsen.selectMediasStream(player).subscribe(this.mediasUpdated$);

    const loadMetadata$ = Nielsen.createLoadMetadataStream(
      player,
      this.mediasUpdated$,
      this.isShifting$
    );

    const initialPlayheadPosition$ = Nielsen.createInitialPlayheadPositionStream(player, loadMetadata$).pipe(
      take(1),
      map((initialPlayheadPosition) => ({
        eventName: NIELSEN_EVENT_SET_PLAYHEAD_POSITION,
        data: initialPlayheadPosition
      }))
    );

    const playheadPosition$ = Nielsen.createPlayheadPositionStream(
      player,
      loadMetadata$
    );

    const userActions$ = Nielsen.createUserActionStream(this.events$, playheadPosition$);

    loadMetadata$
      .pipe(
        switchMap((loadMetadata) => merge(
          of(loadMetadata),
          initialPlayheadPosition$,
          playheadPosition$,
          userActions$
        ).pipe(
          Nielsen.filterSubsequentsPlayheadPositions(),
          takeWhile((hit) => hit.eventName !== NIELSEN_EVENT_END, true)
        ))
      )
      .subscribe(this.triggerEvents.bind(this));
  }

  static disposedEventToNielsenEventEnd(events$) {
    return events$.pipe(
      filter((event) => event === DISPOSED),
      map(() => NIELSEN_EVENT_END)
    );
  }

  static filterSubsequentsPlayheadPositions() {
    return pipe(
      slidingBufferCount(3),
      map(([secondMostRecent, mostRecent, current]) => {
        if (
          mostRecent?.eventName === NIELSEN_EVENT_SET_PLAYHEAD_POSITION
          && current?.eventName === NIELSEN_EVENT_SET_PLAYHEAD_POSITION
          && mostRecent?.data === current?.data
        ) return null;

        if (
          secondMostRecent?.eventName === NIELSEN_EVENT_SET_PLAYHEAD_POSITION
          && current?.eventName === NIELSEN_EVENT_SET_PLAYHEAD_POSITION
          && secondMostRecent?.data === current?.data
        ) return null;

        return current;
      }),
      filter((event) => event !== null)
    );
  }

  static createUserActionStream(events$, playheadPosition$) {
    return events$.pipe(
      withLatestFrom(playheadPosition$),
      map(([eventName, { data: position }]) => ({
        eventName,
        data: position
      })),
      distinctUntilChanged(
        (previous, current) => previous.eventName === current.eventName
          && previous.data === current.data
      )
    );
  }

  static createLoadMetadataStream(
    { events$, playerConfig$ },
    mediasUpdated$,
    isShifting$
  ) {
    return Nielsen.mediaChangedStream(mediasUpdated$, isShifting$, events$).pipe(
      debounceTime(120),
      withLatestFrom(playerConfig$),
      map(([[media, shifting], config]) => {
        const nielsenNoApid = { ...media.markers.nielsen };
        delete nielsenNoApid.apid;
        return ({
          eventName: NIELSEN_EVENT_LOADEDMETADATA,
          data: {
            ...nielsenNoApid,
            cli_md: Nielsen.getCliMd(media, shifting),
            nol_p12: Nielsen.getNolP12(media, config)
          }
        });
      })
    );
  }

  static createInitialPlayheadPositionStream({ medias$, playerConfig$ }, loadmetadata$) {
    return (loadmetadata$.pipe(
      withLatestFrom(playerConfig$, medias$),
      filter(([, , { isLive }]) => !isLive),
      map(([, { startTimecode = 0 }]) => Math.max(startTimecode, 0))
    ));
  }

  static createFirstPlayStream({ commands$, isLive$ }) {
    return commands$.pipe(
      filter(({ type }) => type === FIRST_PLAY),
      withLatestFrom(isLive$),
      map(([, isLive]) => isLive)
    );
  }

  static haltForMidroll({ interruptor: { interruptions$ } }) {
    return interruptions$.pipe(
      filter(({ status, type }) => status === INTERRUPTION_DID_START && type === ADBREAK_HOOK),
      map(() => NIELSEN_EVENT_STOP)
    );
  }

  static createInitStream({ medias$, playerConfig$ }) {
    return medias$.pipe(
      withLatestFrom(playerConfig$),
      switchMap(([medias, playerConfig]) => (playerConfig.publicId
        ? from(hashMessage(playerConfig.publicId))
        : of(''))
        .pipe(
          map((hemSha1) => ({
            apid: medias.markers.nielsen.apid,
            hem_sha1: playerConfig.consent.nielsen === MONITORING_CONSENTED ? hemSha1 : '',
            optout: Boolean(playerConfig.consent.nielsen !== MONITORING_CONSENTED).toString(),
            enableFpid: Boolean(playerConfig.consent.nielsen === MONITORING_CONSENTED).toString(),
            nol_sdkDebug: NIELSEN_DEBUG // Debug always on until the end of validation process
          }))
        )),
      take(1)
    );
  }

  createSdkInstance({ apid, ...contentMetadataObject }) {
    this.nSdkInstance = this.nlsQ(apid, this.instanceName, contentMetadataObject);
  }

  triggerEvents({ eventName, data }) {
    this.nSdkInstance.ggPM(eventName, data);
  }

  static isShiftingStream(events$, medias$) {
    return medias$.pipe(
      switchMap(() => events$.pipe(
        filter((e) => [TIMESHIFTING_MEDIAMETRIE_SHIFTING, TIMESHIFTING_MEDIAMETRIE_LIVE].includes(e)),
        map((e) => e === TIMESHIFTING_MEDIAMETRIE_SHIFTING),
        startWith(false),
        distinctUntilChanged()
      ))
    );
  }

  static mediaChangedStream(media$, shift$, events$) {
    return media$.pipe(
      switchMap((media) => events$.pipe(
        filter((e) => e === TIMEUPDATE),
        take(1),
        delay(1000),
        switchMap(() => shift$.pipe(
          map((shift) => [media, shift]),
          takeUntil(events$.pipe(filter((e) => e?.name === NEW_CONTENT)))
        ))
      ))
    );
  }

  static getCliMd(media, shifting) {
    return Media.isLive(media)
      ? (shifting && TIMESHIFTING) || LIVE
      : media.markers.nielsen.cli_md;
  }

  static getNolP12(media, { diffusion: { mode, position, length } = {} }) {
    return mode !== 'tunnel' ? media.markers.nielsen?.nol_p12 : `p12,tunnel_${position}_${length}`;
  }

  static selectMediasStream(player) {
    return player.epgController.isEpgable$.pipe(
      switchMap((isEpgable) => (isEpgable
        ? Nielsen.createStreamFromEPG(player)
        : Nielsen.createStreamFromMedias(player)
      ))
    );
  }

  static createStreamFromMedias({ medias$, events$, rendererController: { duration$ } }) {
    return medias$.pipe(
      switchMap((media) => {
        /**
         * We wait for RENDERER_READY in case the media is a raw live url and not an id.
         * isLive and streamDuration are updated in the store by the renderer.
         */
        const ready$ = events$.pipe(filter((e) => e === RENDERER_READY));
        /**
         * this is a rxjs trick to wait for both stream the first time,
         * and then trigger only when one of them changes
         */
        return merge(
          combineLatest(ready$, duration$).pipe(take(1))
          // ready$.pipe(withLatestFrom(duration$)).pipe(skip(1))
        ).pipe(
          take(1),
          map(([, duration]) => {
            if (media.markers.nielsen.length) return media;

            // If no streamDuration is provided, we correct it with the media duration
            const corrected = { ...media };
            corrected.markers.nielsen.length = Math.floor(duration);
            return corrected;
          })
        );
      })
    );
  }

  static createStreamFromEPG({ epgController, medias$ }) {
    return epgController.currentMetadata$
      .pipe(
        withLatestFrom(medias$),
        map(([epg, medias]) => ({
          ...medias,
          meta: {
            ...medias.meta,
            title: epg.title,
            image_url: epg?.image_url,
            pre_title: epg?.pre_title,
            additional_title: epg?.additional_title,
            editorial_title: epg?.editorial_title,
            id: epg.id
          },
          markers: epg?.markers
        }))
      );
  }

  static setDiffusion({ is_live: isLive, is_DVR: isDVR, timeshiftable }) {
    if (isLive && !timeshiftable) return LIVE;
    if (isDVR || timeshiftable) return TIMESHIFTING;
    return REPLAY;
  }

  static createPlayheadPositionStream(
    {
      rendererController: { currentTime$, duration$ },
      isLive$,
      timeshiftable$
    },
    loadMetadata$
  ) {
    return loadMetadata$.pipe(
      concatMap(() => Nielsen.createPlayheadPosition({
        duration$,
        currentTime$,
        isLive$,
        timeshiftable$
      }))
    );
  }

  static createStopAndGoEvent({ player, mediasUpdated$, isShifting$ }) {
    const notif$ = merge(
      /* Ensure first eStat notification is a play or pause */
      player.events$.pipe(
        filter((evt) => [PLAYBACK_PLAYING, PLAYBACK_PLAY, PLAYBACK_PAUSE].includes(evt)),
        exhaustMap((evt) => player.events$.pipe(startWith(evt))),
        // filter events are handled independently
        filter((evt) => ![PLAYBACK_PAUSE, EPG_CURRENT_START, TIMESHIFTING_MEDIAMETRIE_LIVE, TIMESHIFTING_MEDIAMETRIE_SHIFTING].includes(evt))
      ),
      // Send pause only when it is not a pause from a seek session
      player.events$.pipe(
        withLatestFrom(player.userEvents$),
        filter(([evt, { action }]) => action !== USER_SEEK_START && evt === PLAYBACK_PAUSE),
        map(() => PLAYBACK_PAUSE)
      ),
      isShifting$.pipe(map((shifting) => (shifting ? TIMESHIFTING_MEDIAMETRIE_SHIFTING : TIMESHIFTING_MEDIAMETRIE_LIVE))),
      player.errors$.pipe(
        filter(({ error }) => error.fatal),
        map(() => PLAYER_ERROR)
      ),
      /**
       * A WAITING event may follow a SEEK event if the video hasn't fully buffered.
       * This will cause a PAUSE notification on mediametrie which will never resolve if there
       * are no subsequent PLAYING events.
       * => in that case : check if videoTag is paused on SEEKED event and emit PLAYBACK_PLAY
       * to notify eStat that video playback has resumed.
       * => inconsistent event order on Safari :
       * delay emission in case SEEKED event happens before WAITING event
       */
      player.commandController.commands$.pipe(
        filter(({ type }) => [SEEK, PAUSE].includes(type)),
        map(() => SEEK)
      ),
      player.events$.pipe(
        filter((e) => [EPG_CURRENT_START].includes(e)),
        /* When epg changed we only send trigger the interval when the video is playing */
        switchMap((epgCurrentStart) => player.events$.pipe(
          filter((ev) => [PLAYBACK_PLAYING, PLAYBACK_PLAY].includes(ev)),
          take(1),
          map(() => epgCurrentStart)
        ))
      )
    ).pipe(
      filter((event) => allowedEvents.includes(event))
    );

    return player.freewheelController.isAd$.pipe(switchMap(({ isAd }) => (isAd ? NEVER : combineLatest(notif$, mediasUpdated$).pipe(
      filter(([event, { video }]) => Object.keys(EVENT_MAPPING[Nielsen.setDiffusion(video)]).includes(event)),
      map(([event, { video }]) => EVENT_MAPPING[Nielsen.setDiffusion(video)][event])
    ))));
  }

  static createEventNotificationStream = ({ player, mediasUpdated$, isShifting$ }) => player.events$.pipe(
    filter((e) => e === VIDEO_START),
    switchMap(() => Nielsen.createStopAndGoEvent({ player, mediasUpdated$, isShifting$ }))
  );

  static createPlayheadPosition({ duration$, currentTime$, isLive$, timeshiftable$ }) {
    return currentTime$
      .pipe(
        map((currentTime) => Math.floor(currentTime)),
        distinctUntilChanged()
      )
      .pipe(
        withLatestFrom(duration$, isLive$, timeshiftable$),
        filter(([, duration, isLive, timeshiftable]) => !(isLive && timeshiftable && duration === 0)),
        map(([currentTime, duration, isLive, timeshiftable]) => (isLive
          /* For live not timeshiftable we do not need to substract the delta between duration and currentTime */
          ? Math.floor((Date.now() / 1000) - (timeshiftable ? (duration - currentTime) : 0))
          : currentTime
        )),
        map((playheadPosition) => ({
          eventName: NIELSEN_EVENT_SET_PLAYHEAD_POSITION,
          data: playheadPosition
        }))
      );
  }

  dispose() {
    delete window[NIELSEN_LIB_NAME][this.instanceName];
    super.dispose();
  }
}
